import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { StatusBadge, DemoBadge } from "@/components/billing/StatusBadge";

const d = (v) => (v ? new Date(v).toLocaleDateString() : "-");
const OPEN = ["CREATED", "AWAITING_PAYMENT", "CONFIRMING", "PARTIALLY_PAID"];

export function BillingSection() {
  const [b, setB] = useState(null);
  const load = () => client.get("/billing/me").then((r) => setB(r.data));
  useEffect(() => { load(); }, []);
  if (!b) return null;
  const toggle = async (s, v) => { await client.post(`/billing/subscriptions/${s.id}/reminders`, { enabled: v }); load(); };
  const memberships = b.subscriptions.filter((s) => s.type !== "analyst");
  const analysts = b.subscriptions.filter((s) => s.type === "analyst");
  const pending = b.orders.filter((o) => OPEN.includes(o.status));

  const SubCard = ({ s }) => (
    <div data-testid={`sub-card-${s.id}`} className="rounded-xl border border-white/10 p-4 flex flex-wrap items-center gap-4">
      <div className="flex-1 min-w-[180px]">
        <p className="font-semibold">{s.product_name}{s.source === "promo" && <span className="ml-2 text-[10px] uppercase text-fuchsia-300">complimentary</span>}</p>
        {s.analyst && <p className="text-xs text-zinc-500">{s.analyst.name}</p>}
        <p className="text-xs text-zinc-500 mt-1">Started {d(s.started_at)} · Expires {d(s.expires_at)}</p>
      </div>
      <StatusBadge status={s.status} />
      {s.source !== "promo" && <label className="flex items-center gap-2 text-xs text-zinc-400">Reminders<Switch data-testid="sub-reminders" checked={s.reminders_enabled !== false} onCheckedChange={(v) => toggle(s, v)} /></label>}
      <Link to={`/checkout/${s.product_id}`} data-testid="sub-renew" className="px-4 py-2 rounded-full border border-[#ccff00]/50 text-[#ccff00] text-xs font-semibold">Renew</Link>
    </div>
  );

  return (
    <div className="space-y-10" data-testid="billing-section">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h2 className="font-display text-lg font-bold">My memberships</h2>
        <Link to="/membership" data-testid="browse-plans" className="text-sm text-[#ccff00] hover:underline">Browse plans</Link>
      </div>
      {b.alpha.categories.length > 0 && memberships.length === 0 && <p className="text-sm text-zinc-400">Alpha access active{b.alpha.expires_at ? ` until ${d(b.alpha.expires_at)}` : ""}.</p>}
      <div className="space-y-3">{memberships.map((s) => <SubCard key={s.id} s={s} />)}{memberships.length === 0 && b.alpha.categories.length === 0 && <p className="text-sm text-zinc-500">No membership yet.</p>}</div>
      <div>
        <h2 className="font-display text-lg font-bold mb-3">My analysts</h2>
        <div className="space-y-3">
          {analysts.map((s) => <SubCard key={s.id} s={s} />)}
          {b.legacy_analyst_subs.map((s, i) => <p key={i} className="text-sm text-zinc-400">{s.analyst?.name} · QRIS subscription until {d(s.ends_at)}</p>)}
          {analysts.length === 0 && b.legacy_analyst_subs.length === 0 && <p className="text-sm text-zinc-500">No analyst subscriptions.</p>}
        </div>
      </div>
      {pending.length > 0 && (
        <div>
          <h2 className="font-display text-lg font-bold mb-3">Pending payments</h2>
          {pending.map((o) => (
            <Link key={o.id} to={`/checkout/${o.product_id}?order=${o.id}`} data-testid={`pending-${o.id}`} className="flex items-center gap-3 rounded-xl border border-white/10 p-4 hover:border-white/30">
              <span className="flex-1 text-sm">{o.product_snapshot.name} · {o.pay_amount} {o.pay_currency?.toUpperCase()}</span>{o.is_demo && <DemoBadge />}<StatusBadge status={o.status} />
            </Link>
          ))}
        </div>
      )}
      <div>
        <h2 className="font-display text-lg font-bold mb-3">Billing history</h2>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm" data-testid="billing-history">
            <thead className="text-[10px] font-mono-cl uppercase tracking-[0.14em] text-zinc-500"><tr>{["Date", "Plan", "Amount", "Network", "Status", "Receipt"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
            <tbody>
              {b.orders.map((o) => (
                <tr key={o.id} className="border-t border-white/5">
                  <td className="px-3 py-2 whitespace-nowrap">{d(o.created_at)}</td>
                  <td className="px-3 py-2">{o.product_snapshot.name} {o.is_demo && <DemoBadge />}</td>
                  <td className="px-3 py-2 whitespace-nowrap">${o.price_usd}{o.pay_amount ? ` · ${o.pay_amount} ${o.pay_currency?.toUpperCase()}` : ""}</td>
                  <td className="px-3 py-2">{o.network_name}</td>
                  <td className="px-3 py-2"><StatusBadge status={o.status} /></td>
                  <td className="px-3 py-2 text-xs text-zinc-500 font-mono-cl">{o.status === "PAID" ? `${o.id} · ${o.provider_payment_id}` : "-"}</td>
                </tr>
              ))}
              {b.orders.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-zinc-500">No payments yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
